"use client";

import React from "react";
import { IWhatForBlock } from "@/page-builder/fragments/WhatForBlock/types";
import { motion } from "framer-motion";
import Heading from "@/components/molecules/Heading";
import BupiLinkButton from "@/components/atoms/linkButton";
import Image from "@/components/atoms/image";
import WrapperBlueStyle from "@/components/atoms/wrapper-blue-style";
import { convertLexicalWithInternalLinks } from "@/lib/lexical";
import styles from "./WhatForBlock.module.css";
import { useTranslations } from "next-intl";
import { getAccessibleImageProps } from "@/lib/image-alt";

export const WhatForBlock: React.FC<IWhatForBlock> = ({
  mainTitle,
  mainDescription,
  mainImage,
  secondaryTitle,
  secondaryDescription,
  secondaryImage,
  ctaButton,
  appleStoreURL,
  additionalTitle1,
  additionalDescription1,
  additionalTitle2,
  additionalDescription2,
  background,
  googlePlayStoreURL,
  accessType
}) => {
  const t = useTranslations("DigitalChannelsBlock");
  const sectionClass =
    background !== "default"
      ? styles["land-registration-guide"] +
      " bg-primary-100 mb-[-90px] pt-64 md:pt-96 pb-[120px] md:pb-[200px]"
      : " py-64 md:py-96";

  const Content = (
    <section
      className={`text-[#2B363C] space-y-32 md:space-y-64 ${sectionClass}`}
    >
      <div className="mx-auto container flex flex-col gap-32">
        <div className="grid grid-cols-1 xl:grid-cols-8 md:items-center gap-32">
          <div className="relative flex col-span-4 justify-end order-1 xl:order-2">
            {mainImage?.url && (
              <motion.img
                src={mainImage.url}
                {...getAccessibleImageProps(mainTitle)}
                className="w-full max-w-[296px] md:max-w-[468px] xl:max-w-[592px] h-auto z-0 mx-auto"
                initial={{ opacity: 0, x: -80 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  x: { duration: 2, ease: "easeOut" },
                  opacity: { duration: 1.5 },
                }}
              />
            )}
          </div>
          <div className="flex col-span-4 flex-col justify-center space-y-8 order-1">
            {mainTitle && <Heading title={mainTitle} />}
            {mainDescription && (
              <div
                dangerouslySetInnerHTML={{
                  __html: convertLexicalWithInternalLinks(mainDescription as any),
                }}
                className="text-l-regular py-8 prose prose-ul:my-0 marker:text-neutral-900"
              />
            )}

          </div>
        </div>

        {(secondaryTitle || secondaryDescription || secondaryImage) && (
          <div className="grid grid-cols-1 xl:grid-cols-8 md:items-center gap-32 xl:mt-32">
            <div className="relative flex col-span-4 justify-end">
              {secondaryImage?.url && (
                <motion.img
                  src={secondaryImage.url}
                  {...getAccessibleImageProps("BUPI")}
                  className="w-full max-w-[296px] md:max-w-[468px] xl:max-w-[592px] h-auto z-0 mx-auto"
                  initial={{ opacity: 0, x: 80 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    x: { duration: 2, ease: "easeOut" },
                    opacity: { duration: 1.5 },
                  }}
                />
              )}
            </div>
            <div className="flex col-span-4 flex-col justify-center space-y-8 items-start">
              {secondaryTitle && <Heading title={secondaryTitle} />}
              {secondaryDescription && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertLexicalWithInternalLinks(secondaryDescription as any),
                  }}
                  className="text-l-regular py-8 prose prose-ul:my-0 marker:text-neutral-900"
                />
              )}
              {ctaButton && accessType !== "appsStore" &&  ( 
                <BupiLinkButton
                  label={ctaButton.label}
                  link={ctaButton.url}
                  target={ctaButton?.target || "_self"}
                  hasIcon
                  leadingIcon="agora-line-arrow-right-circle"
                  className=""
                />
              )}

              {(appleStoreURL || googlePlayStoreURL) && (accessType === "appsStore") &&(
                <div className="flex flex-col md:flex-row gap-24 xl:gap-32 items-start flex-wrap w-full">
                  {googlePlayStoreURL && (
                    <a
                      href={googlePlayStoreURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full h-[60px] md:max-w-[202px]"
                    >
                      <div className="w-full h-full reltative flex items-center justify-center">
                        <Image
                          width={202}
                          height={60}
                          src="/assets/app/google-play.svg"
                          alt={t("info")}
                          className="object-contain"
                        />
                      </div>
                    </a>
                  )}
                  {appleStoreURL && (
                    <a
                      href={appleStoreURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full h-[60px] md:max-w-[202px]"
                    >
                      <div className="w-full h-full reltative flex items-center justify-center">
                        <Image
                          width={202}
                          height={60}
                          src="/assets/app/app-store.svg"
                          alt={t("info2")}
                          className="object-contain"
                        />
                      </div>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-32 mt-32">
          {(additionalTitle1 || additionalDescription1) && (
            <div className="w-full md:w-1/2 space-y-4 ">
              {additionalTitle1 && (
                <h3 className="text-l-bold md:text-xl-bold xl:text-2xl-bold">
                  {additionalTitle1}
                </h3>
              )}
              {additionalDescription1 && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertLexicalWithInternalLinks(additionalDescription1 as any),
                  }}
                  className="text-l-regular py-8 prose prose-ul:my-0 marker:text-neutral-900"
                />
              )}

            </div>
          )}
          {(additionalTitle2 || additionalDescription2) && (
            <div className="w-full md:w-1/2 space-y-4">
              {additionalTitle2 && (
                <h3 className="text-l-bold md:text-xl-bold xl:text-2xl-bold">
                  {additionalTitle2}
                </h3>
              )}
              {additionalDescription2 && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertLexicalWithInternalLinks(additionalDescription2 as any),
                  }}
                  className="text-l-regular py-8 prose prose-ul:my-0 marker:text-neutral-900"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  return background === "default" ? <WrapperBlueStyle>{Content}</WrapperBlueStyle> : Content;
};
